import React, { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { AuthDetails } from '../libs/authDetails'
import { getAllUsersTripDetails, getVehicleType } from '../services/ApiServices'
import { useTranslation } from 'react-i18next'
import Header from '../components/Header'
import { TripDetail, VehicleType } from '../types/userList'
import AuthChecker from '../libs/AuthChecker'
import moment from 'moment'
import Button from '../components/Button'
import TripTable from '../components/userList/TripTable'
import TripFilters from '../components/userList/TripFilters'

const UserList = () => {
  const { token } = AuthDetails()
  const { t } = useTranslation()

  const getCurrentDate = () => {
    return moment().format('YYYY-MM-DD');
  };

  const [filters, setFilters] = useState({
    date: getCurrentDate(),
    vehicleType: '',
    limit: 10,
    offset: 0
  })

  // Add state for date range
  const [dateRange, setDateRange] = useState({
    startDate: getCurrentDate(),
    endDate: getCurrentDate()
  });

  const [tripData, setTripData] = useState<TripDetail[]>([])
  const [totalTrips, setTotalTrips] = useState<number>(0)
  const [isLoad, setIsLoad] = useState<any>(false)
  const [isLoadExport, setIsLoadExport] = useState<any>(false)

  const fetchTripDetails = async (): Promise<{
    trip_details: TripDetail[]
    total_trips: number
  }> => {
    const { vehicleType, limit, offset } = filters
    const { startDate, endDate } = dateRange;

    const response = await getAllUsersTripDetails(
      startDate,
      endDate,
      vehicleType,
      limit,
      offset,
      token
    )
    return response
  }

  const {
    data: tripDetailsResponse,
    isLoading,
    error,
    refetch
  } = useQuery<{ trip_details: TripDetail[]; total_trips: number }>(
    ['tripData', filters, dateRange],
    fetchTripDetails,
    {
      keepPreviousData: true
    }
  )
console.log('isLoading', isLoading)
  const fetchVehicleTypes = async (): Promise<VehicleType[]> => {
    const data = await getVehicleType(token)
    return data
  }

  const {
    data: vehicleTypes = []
  } = useQuery<VehicleType[], Error>('vehicleTypes', fetchVehicleTypes)

  useEffect(() => {
    if (tripDetailsResponse) {
      const { trip_details, total_trips } = tripDetailsResponse
      setTotalTrips(total_trips)
      if (filters.offset === 0) {
        setTripData(trip_details)
        setIsLoad(false)
      } else {
        setTripData((prevData) => [...prevData, ...trip_details])
        setIsLoad(false)
      }
    }
  }, [tripDetailsResponse])

  const handleLoadMore = () => {
    setIsLoad(true)
    if (tripData?.length < totalTrips) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        offset: prevFilters.offset + prevFilters.limit
      }))
    }
  }

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: string
  ) => {
    if (key === 'vehicleType') {
      const selectedVehicleType = e.target.value;
      setFilters({ ...filters, vehicleType: selectedVehicleType, offset: 0 });
    } else if (key === 'date') {
      const selectedDate = e.target.value;
      setFilters({ ...filters, date: selectedDate, offset: 0 });
      setDateRange({...dateRange, startDate: selectedDate});
    } else {
      setFilters({ ...filters, [key]: e.target.value, offset: 0 });
    }
  };
  const handleDateRangeSearch = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
    setFilters({ ...filters, offset: 0 });
    refetch();
  };
  const handleClearFilters = () => {
    setFilters({
      date: getCurrentDate(),
      vehicleType: '',
      limit: 10,
      offset: 0
    });

    setDateRange({
      startDate: '',
      endDate: ''
    });

    // setIncludeDateParams(false);
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('selectionType');
    // setResetCounter(prev => prev + 1);
    // refetch();
  };
  const handleExportButtonClick = async () => {
    setIsLoadExport(true)
    try {
      const { startDate, endDate } = dateRange;
      const response = await getAllUsersTripDetails(
        startDate,
        endDate,
        filters.vehicleType,
        totalTrips,
        0,
        token
      );

      if (response && response.trip_details.length > 0) {
        const csvContent = convertToCSV(response.trip_details);
        const link = document.createElement('a');
        link.setAttribute('href', csvContent);
        link.setAttribute('download', 'tripData.csv');
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      console.error("Error exporting trip data:", error);
    }finally{
      setIsLoadExport(false)
    }
  };

  const convertToCSV = (data: TripDetail[]) => {
    const headers = [
      t('Sno', { lng: 'ger' }),
      t('userName', { lng: 'ger' }),
      t('First_Name', { lng: 'ger' }),
      t('co_driver', { lng: 'ger' }),
      t('Category', { lng: 'ger' }),
      t('sub_category', { lng: 'ger' }),
      t('Tour_Id', { lng: 'ger' }),
      t('garbageType', { lng: 'ger' }),
      t('VehicleNo', { lng: 'ger' }),
      t('Start_End_Time', { lng: 'ger' }),
      t('Total_Break_Time', { lng: 'ger' }),
      t('Break_Details', { lng: 'ger' }),
      t('net_Working_Time', { lng: 'ger' }),
      t('PhoneNo', { lng: 'ger' }),
      t('diesel_filled_up', { lng: 'ger' }),
      t('Location', { lng: 'ger' }),
      t('postal_code', { lng: 'ger' }),
      t('start_mileage', { lng: 'ger' }),
      t('final_mileage', { lng: 'ger' }),
      t('km_driven', { lng: 'ger' }),
      t('AdBlue', { lng: 'ger' }),
      t('accident', { lng: 'ger' }),
    ];

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      headers.join(',') +
      '\n' +
      data
        ?.map((item, index) => {
          const startEndTime =
            (moment(item.check_in_time).isValid() ? moment(item.check_in_time).format('HH:mm') : '-') +
            ' - ' +
            (moment(item.check_out_time).isValid() ? moment(item.check_out_time).format('HH:mm') : '-');

          let totalBreakTime = '-';
          let breakDetailsFormatted = '-';

          if (item.break_start_time && item.break_end_time) {
            const breakStarts = item.break_start_time.split(',').map((time: string) => time.trim());
            const breakEnds = item.break_end_time.split(',').map((time: string) => time.trim());

            const minLength = Math.min(breakStarts.length, breakEnds.length);
            let totalBreakMinutes = 0;

            const breakDetails = [];
            for (let i = 0; i < minLength; i++) {
              if (breakStarts[i] && breakEnds[i]) {
                const breakStart = moment(breakStarts[i], 'HH:mm');
                const breakEnd = moment(breakEnds[i], 'HH:mm');

                let breakMins = breakEnd.diff(breakStart, 'minutes');
                if (breakMins < 0) {
                  breakMins = breakEnd.add(1, 'days').diff(breakStart, 'minutes');
                }

                totalBreakMinutes += breakMins;

                breakDetails.push(`Break ${i + 1}: ${breakStarts[i].slice(0, 5)} - ${breakEnds[i].slice(0, 5)}`);
              }
            }

            breakDetailsFormatted = breakDetails.length > 0 ? `"${breakDetails.join('; ')}"` : '-';

            const hours = Math.floor(totalBreakMinutes / 60);
            const minutes = totalBreakMinutes % 60;

            totalBreakTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
          }

          let netWorkingTime = '-';
          if (item.check_in_time && item.check_out_time) {
            const startTime = moment(item.check_in_time);
            const endTime = moment(item.check_out_time);
            const totalMinutes = endTime.diff(startTime, 'minutes');

            let breakDuration = 0;
            if (item.break_start_time && item.break_end_time) {
              const breakStarts = item.break_start_time.split(',').map((time: string) => time.trim());
              const breakEnds = item.break_end_time.split(',').map((time: string) => time.trim());

              const minLength = Math.min(breakStarts.length, breakEnds.length);

              for (let i = 0; i < minLength; i++) {
                if (breakStarts[i] && breakEnds[i]) {
                  const breakStart = moment(breakStarts[i], 'HH:mm');
                  const breakEnd = moment(breakEnds[i], 'HH:mm');

                  let breakMins = breakEnd.diff(breakStart, 'minutes');
                  if (breakMins < 0) {
                    breakMins = breakEnd.add(1, 'days').diff(breakStart, 'minutes');
                  }

                  breakDuration += breakMins;
                }
              }
            }

            const netMinutes = totalMinutes - breakDuration;
            const hours = Math.floor(netMinutes / 60);
            const minutes = netMinutes % 60;

            netWorkingTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
          }

          const coDrivers = item.co_driver ? `"${item.co_driver.split(',').join(', ')}"` : '-';

          const kmDriven =
            item.final_mileage && item.start_mileage
              ? Number(item.final_mileage) - Number(item.start_mileage)
              : '-';

          return [
            index + 1,
            item.username || '-',
            `${item.firstname || '-'} ${item.lastname || '-'}`,
            coDrivers,
            item.category || '-',
            item.sub_category || '-',
            item.trip_id || '-',
            `"${renderGarbageTypes(item) || '-'}"`,
            item.vehicleNumber || '-',
            startEndTime,
            totalBreakTime,
            breakDetailsFormatted,
            netWorkingTime,
            item.phonenumber || '-',
            item.diesel_filled_up || '-',
            item.location || '-',
            item.postal_code || '-',
            item.start_mileage || '-',
            item.final_mileage || '-',
            kmDriven,
            item.reFuel || '-',
            item.damaged === 0 ? 'No' : 'Yes',
          ].join(',');
        })
        .join('\n');

    return encodeURI(csvContent);
  };

  const renderGarbageTypes = (trip: TripDetail) => {
    if (trip && trip.garbageType) {
      return trip.garbageType
        .replace('[', '')
        .replace(']', '')
        .split(',')
        .map((type: string) => type?.trim())
        .join(', ')
    }
    return ''
  }

  AuthChecker()
  return (
    <>
      <Header style={{ paddingTop: "16px", paddingBottom: "16px" }} />
      <div className="mb-3 overFlow p-3 admin">
        <TripFilters
          filters={filters}
          vehicleTypes={vehicleTypes}
          onFilterChange={handleFilterChange}
          onExportClick={handleExportButtonClick}
          hasData={tripData?.length > 0}
          t={t}
          onDateRangeSearch={handleDateRangeSearch}
          onClearFilters={handleClearFilters}
          isLoading={isLoadExport}      />
        <TripTable
          tripData={tripData}
          isLoading={isLoading}
          isLoadMore={isLoad}
          error={!!error}
          renderGarbageTypes={renderGarbageTypes}
          t={t}
        />
        {!isLoad && tripData?.length < totalTrips && (
          <div className="text-white mt-3 d-flex align-items-center justify-content-center">
            <Button
              title={t('loadMore')}
              onButtonClick={handleLoadMore}
              backgroundColor="#05653F"
              color="white"
            />
          </div>
        )}
      </div>
    </>
  )
}

export default UserList